// src/components/ui/RichTextEditor.jsx
// Tiny WYSIWYG editor for the email template body.
//
// Two modes:
//   • Visual — contentEditable surface with a toolbar.
//   • HTML   — raw textarea for power users.
//
// We use document.execCommand for the visual mode formatting. It IS
// deprecated, but: (a) every browser still supports it, (b) the alternative
// is bringing in TipTap/Quill which adds ~50 KB gzipped, (c) HTML mode is
// the fallback for anything the visual editor doesn't handle cleanly.
//
// The editor stores its content as HTML so save/load is trivial — same
// shape the send pipeline expects.

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bold, Italic, Underline, Link as LinkIcon, Image as ImageIcon,
  List, ListOrdered, Heading2, Heading3, Code,
  AlignLeft, AlignCenter, AlignRight, Eraser, Eye,
} from 'lucide-react';

function ToolbarButton({ onClick, title, children, active = false, disabled = false }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}    // keep editor focus
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40 ${active ? 'bg-muted' : ''}`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, height = 320, placeholder = '' }) {
  const [mode, setMode]   = useState('visual');   // 'visual' | 'html'
  const editorRef = useRef(null);

  // Hydrate the contentEditable surface with the incoming value, but only
  // when the user is NOT mid-edit (avoids resetting cursor).
  useEffect(() => {
    if (mode !== 'visual') return;
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, mode]);

  const exec = useCallback((command, arg = null) => {
    document.execCommand(command, false, arg);
    // After exec, push the new HTML up.
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const insertLink = () => {
    const url = window.prompt('Link URL', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Image URL (https://…)');
    if (!url) return;
    exec('insertImage', url);
  };

  const insertMergeTag = (tag) => {
    exec('insertText', `{{${tag}}}`);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-border bg-muted/40">
        <ToolbarButton title="Bold"          onClick={() => exec('bold')}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Italic"        onClick={() => exec('italic')}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Underline"     onClick={() => exec('underline')}><Underline className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarButton title="Heading 2"     onClick={() => exec('formatBlock', 'H2')}><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Heading 3"     onClick={() => exec('formatBlock', 'H3')}><Heading3 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Paragraph"     onClick={() => exec('formatBlock', 'P')}><span className="text-xs font-semibold px-1">P</span></ToolbarButton>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarButton title="Bullet list"   onClick={() => exec('insertUnorderedList')}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => exec('insertOrderedList')}><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarButton title="Align left"    onClick={() => exec('justifyLeft')}><AlignLeft className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Align center"  onClick={() => exec('justifyCenter')}><AlignCenter className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Align right"   onClick={() => exec('justifyRight')}><AlignRight className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarButton title="Insert link"   onClick={insertLink}><LinkIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Insert image"  onClick={insertImage}><ImageIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Clear formatting" onClick={() => exec('removeFormat')}><Eraser className="h-4 w-4" /></ToolbarButton>

        {/* Merge-tag dropdown */}
        <div className="relative ml-1">
          <select
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => { if (e.target.value) { insertMergeTag(e.target.value); e.target.value = ''; } }}
            className="text-xs h-7 border border-border rounded bg-card px-1"
            defaultValue=""
          >
            <option value="">Merge tag…</option>
            <option value="first_name">{'{{first_name}}'}</option>
            <option value="last_name">{'{{last_name}}'}</option>
            <option value="email">{'{{email}}'}</option>
            <option value="custom.tier">{'{{custom.tier}}'}</option>
            <option value="custom.role">{'{{custom.role}}'}</option>
          </select>
        </div>

        <div className="flex-1" />

        {/* Visual / HTML toggle */}
        <ToolbarButton
          title={mode === 'visual' ? 'View source HTML' : 'Visual editor'}
          onClick={() => setMode(m => (m === 'visual' ? 'html' : 'visual'))}
          active={mode === 'html'}
        >
          {mode === 'visual' ? <Code className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </ToolbarButton>
      </div>

      {/* Editor surface */}
      {mode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          onBlur={(e)  => onChange(e.currentTarget.innerHTML)}
          className="p-4 text-sm leading-relaxed focus:outline-none overflow-y-auto"
          style={{ minHeight: height, maxHeight: height * 2 }}
          data-placeholder={placeholder}
        />
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.floor(height / 22)}
          className="w-full p-4 font-mono text-xs bg-card text-foreground focus:outline-none"
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

export default RichTextEditor;
